/**
 * Toggle — switch with a 40 × 22 px track + 18 px thumb (theme T5a §7).
 *
 * Label is the thing being controlled (e.g. "Email notifications"), not
 * the state (don't write "Enable email notifications").  Keep that rule
 * at the callsite — the component only renders the switch.
 *
 * Focus ring renders on the visible track via `has-[input:focus-visible]`
 * since the `<input>` itself is `sr-only` (F6 audit F6-01).
 *
 * A click on the visible track toggles the switch (2026-10-03 review §2
 * #19: it did nothing unless a `<label>` wrapped it). Inside a `<label>`
 * the browser already forwards the click, so the track does not click
 * twice. Pass `aria-label` when no visible label names the switch.
 */
import type { InputHTMLAttributes, MouseEvent } from 'react';
import { forwardRef, useRef } from 'react';

import { cn } from './cn';

export type ToggleProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'>;

export const Toggle = forwardRef<HTMLInputElement, ToggleProps>(function Toggle(
  { className, checked, disabled, ...rest },
  ref,
) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const setRefs = (el: HTMLInputElement | null): void => {
    inputRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) ref.current = el;
  };
  const onTrackClick = (e: MouseEvent<HTMLSpanElement>): void => {
    if (disabled || e.target === inputRef.current) return;
    // A wrapping <label> forwards the click to the input itself.
    if (e.currentTarget.closest('label')) return;
    inputRef.current?.click();
  };
  return (
    <span
      className={cn(
        // Track — 40 × 22 via inline style.  h-* class omitted because
        // Tailwind has no 5.5 step; inline style is authoritative (F6-05).
        'duration-DEFAULT relative inline-flex w-10 cursor-pointer items-center rounded-full transition-colors ease-standard',
        'has-[input:focus-visible]:ring-2 has-[input:focus-visible]:ring-brand-primary-400 has-[input:focus-visible]:ring-offset-2',
        checked ? 'bg-brand-primary-600' : 'bg-slate-300',
        disabled && 'cursor-not-allowed opacity-disabled',
        className,
      )}
      style={{ height: 22 }}
      onClick={onTrackClick}
    >
      <input
        ref={setRefs}
        checked={checked}
        className="sr-only"
        disabled={disabled}
        role="switch"
        type="checkbox"
        {...rest}
      />
      <span
        aria-hidden
        className={cn(
          // Thumb — 18 × 18 via inline style.
          'duration-DEFAULT absolute left-0.5 rounded-full bg-white shadow-1 transition-transform ease-standard',
          checked ? 'translate-x-[18px]' : 'translate-x-0',
        )}
        style={{ width: 18, height: 18 }}
      />
    </span>
  );
});
